import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  Chip,
  Tooltip,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
  Business as CompanyIcon,
  Apartment as TenantIcon,
  Contacts as ContactsIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const openPopup = (path) => {
  const features = 'noopener,noreferrer,width=1280,height=860,menubar=no,toolbar=no,location=yes,status=yes,resizable=yes,scrollbars=yes';
  const win = window.open(path, '_blank', features);
  if (!win) {
    toast.error('Popup blocked. Allow popups for this site to open tenant windows.');
  }
};

const TreeNode = ({
  label,
  secondary,
  icon,
  chip,
  children,
  defaultOpen = false,
  actions
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = React.Children.count(children) > 0;

  return (
    <Box>
      <ListItemButton
        onClick={() => hasChildren && setOpen((v) => !v)}
        sx={{
          borderRadius: 1,
          mb: 0.25,
          py: 0.5,
          alignItems: 'flex-start',
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
          {hasChildren ? (open ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />) : (
            <Box sx={{ width: 20 }} />
          )}
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 36, mt: 0.25, color: 'primary.main' }}>{icon}</ListItemIcon>
        <ListItemText
          primary={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
              {chip}
            </Box>
          )}
          secondary={secondary}
        />
        {actions && (
          <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
            {actions}
          </Box>
        )}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 3, borderLeft: '2px solid', borderColor: 'divider', ml: 2.5 }}>
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

/**
 * Company → Tenants / Contacts / Admins tree for the Tenants screen.
 */
const TenantsCompanyTree = ({
  search = '',
  canEdit = false,
  canDelete = false,
  onDeleteTenant,
  onViewTenant
}) => {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [accRes, tenRes, conRes] = await Promise.all([
          api.get('/accounts', { params: { page: 1, limit: 500, search: search || undefined } }),
          api.get('/tenants', { params: { page: 1, limit: 500 } }),
          api.get('/contacts', { params: { page: 1, limit: 500 } })
        ]);
        if (cancelled) return;
        setAccounts(accRes.data?.data || []);
        setTenants(tenRes.data?.data || []);
        setContacts(conRes.data?.data || []);
      } catch (err) {
        toast.error('Failed to load company tree');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [search]);

  const tree = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const byAccountId = new Map();

    accounts.forEach((a) => {
      byAccountId.set(a.id, {
        id: a.id,
        name: a.company_name || a.account_name || 'Unnamed company',
        account_code: a.account_code,
        tenants: [],
        contacts: []
      });
    });

    const orphanCompanies = new Map();

    const ensureCompany = (accountId, companyName) => {
      if (accountId && byAccountId.has(accountId)) return byAccountId.get(accountId);
      const key = accountId || `company:${(companyName || 'No company').toLowerCase()}`;
      if (!orphanCompanies.has(key)) {
        orphanCompanies.set(key, {
          id: key,
          name: companyName || 'No company',
          account_code: null,
          tenants: [],
          contacts: []
        });
      }
      return orphanCompanies.get(key);
    };

    tenants.forEach((t) => {
      if (t.deleted_at) return;
      const node = ensureCompany(t.account_id, t.company_name || t.account?.company_name || t.account?.account_name);
      node.tenants.push(t);
    });

    contacts.forEach((c) => {
      const node = ensureCompany(c.account_id, c.account?.company_name || c.account?.account_name || c.company_name);
      node.contacts.push(c);
    });

    let nodes = [...byAccountId.values(), ...orphanCompanies.values()]
      .filter((n) => n.tenants.length > 0 || n.contacts.length > 0 || byAccountId.has(n.id));

    if (q) {
      nodes = nodes.filter((n) => {
        const inName = n.name.toLowerCase().includes(q);
        const inTenants = n.tenants.some((t) =>
          [t.tenant_name, t.tenant_id, t.environment, t.environment_short_name]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        );
        const inContacts = n.contacts.some((c) =>
          [c.first_name, c.last_name, c.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
        );
        return inName || inTenants || inContacts;
      });
    }

    nodes.sort((a, b) => a.name.localeCompare(b.name));
    return nodes;
  }, [accounts, tenants, contacts, search]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tree.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No companies / tenants to show in tree view.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 1.5, maxHeight: 640, overflow: 'auto' }}>
      <List dense disablePadding>
        {tree.map((company) => (
          <TreeNode
            key={company.id}
            defaultOpen={tree.length <= 5}
            icon={<CompanyIcon />}
            label={company.name}
            secondary={company.account_code ? `Account ${company.account_code}` : null}
            chip={(
              <>
                <Chip size="small" label={`${company.tenants.length} tenants`} />
                <Chip size="small" variant="outlined" label={`${company.contacts.length} contacts`} />
              </>
            )}
          >
            <TreeNode
              icon={<TenantIcon fontSize="small" />}
              label="Tenants"
              chip={<Chip size="small" label={company.tenants.length} />}
              defaultOpen
            >
              {company.tenants.map((t) => {
                const admins = Array.isArray(t.admin_users) ? t.admin_users : [];
                const primaryAdmin = t.contact
                  ? `${t.contact.first_name || ''} ${t.contact.last_name || ''}`.trim() || t.contact.email
                  : t.contact_email;
                return (
                  <TreeNode
                    key={t.id}
                    icon={<TenantIcon fontSize="small" />}
                    label={t.tenant_name}
                    secondary={`${t.tenant_id} · ${t.environment_short_name || t.environment || '—'} · ${t.status}`}
                    actions={(
                      <>
                        <Tooltip title="View tenant">
                          <IconButton size="small" onClick={() => onViewTenant?.(t)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title="Edit in new window">
                            <IconButton size="small" onClick={() => openPopup(`/tenants/${t.id}/edit`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && !t.is_base && !t.deleted_at && t.provisioning_status !== 'in_progress' && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDeleteTenant?.(t)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {t.is_base && (
                          <Tooltip title="Base tenant — cannot be deleted">
                            <Chip size="small" label="BASE" sx={{ bgcolor: '#c62828', color: '#fff', height: 22 }} />
                          </Tooltip>
                        )}
                      </>
                    )}
                  >
                    <TreeNode
                      icon={<AdminIcon fontSize="small" />}
                      label="Admins"
                      chip={<Chip size="small" label={admins.length + (primaryAdmin ? 1 : 0)} />}
                    >
                      {primaryAdmin && (
                        <TreeNode
                          icon={<PersonIcon fontSize="small" />}
                          label={primaryAdmin}
                          secondary="Primary contact / tenant admin"
                        />
                      )}
                      {admins.map((a, idx) => (
                        <TreeNode
                          key={`${t.id}-admin-${idx}`}
                          icon={<AdminIcon fontSize="small" />}
                          label={a.name || a.email || 'Admin'}
                          secondary={a.email || null}
                        />
                      ))}
                      {!primaryAdmin && admins.length === 0 && (
                        <TreeNode icon={<PersonIcon fontSize="small" />} label="No admins listed" />
                      )}
                    </TreeNode>
                  </TreeNode>
                );
              })}
              {company.tenants.length === 0 && (
                <TreeNode icon={<TenantIcon fontSize="small" />} label="No tenants" />
              )}
            </TreeNode>

            <TreeNode
              icon={<ContactsIcon fontSize="small" />}
              label="Contacts"
              chip={<Chip size="small" label={company.contacts.length} />}
            >
              {company.contacts.map((c) => (
                <TreeNode
                  key={c.id}
                  icon={<PersonIcon fontSize="small" />}
                  label={`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email}
                  secondary={c.email}
                />
              ))}
              {company.contacts.length === 0 && (
                <TreeNode icon={<PersonIcon fontSize="small" />} label="No contacts" />
              )}
            </TreeNode>
          </TreeNode>
        ))}
      </List>
    </Paper>
  );
};

export default TenantsCompanyTree;
export { openPopup };
